import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import { getAllLogs, getProfile, getLogByDate, getMedications } from '../db/database';
import {
  todayString, getVASColor, getVASLabel, calculateStreak,
  getDaysUntilInsight, getCurrentPollenAlerts, generateInsights,
  calculateTotalSymptomScore, formatDateLong
} from '../utils/helpers';
import { POLLEN_CALENDAR } from '../types';
import type { DailyLog, UserProfile, Medication } from '../types';

export default function Dashboard() {
  const navigate = useNavigate();
  const [todayLog, setTodayLog] = useState<DailyLog | null>(null);
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [allLogs, prof, today, meds] = await Promise.all([
        getAllLogs(),
        getProfile(),
        getLogByDate(todayString()),
        getMedications(),
      ]);
      setLogs(allLogs);
      setProfile(prof || null);
      setTodayLog(today || null);
      setMedications(meds);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div className="page"><div className="loading">Caricamento...</div></div>;

  const streak = calculateStreak(logs);
  const daysToInsight = getDaysUntilInsight(logs.length);
  const month = new Date().getMonth() + 1;
  const pollenAlerts = getCurrentPollenAlerts(month, profile?.knownAllergies || []);
  const insights = generateInsights(logs.slice(-30));

  // Sparkline data (last 7 days)
  const sparkData = [];
  for (let i = 6; i >= 0; i--) {
    const d = format(subDays(new Date(), i), 'yyyy-MM-dd');
    const log = logs.find(l => l.date === d);
    sparkData.push({ date: d, vas: log?.vasScore ?? null });
  }

  // Active pollens this month
  const activePollenList = Object.entries(POLLEN_CALENDAR)
    .filter(([, cal]) => cal[month] && cal[month] !== '')
    .map(([name, cal]) => ({ name, level: cal[month] }))
    .sort((a, b) => {
      const order: Record<string, number> = { 'VERY_HIGH': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
      return (order[b.level] || 0) - (order[a.level] || 0);
    });

  // Prophylactic medications the user should take daily
  const prophylacticMeds = medications.filter(m => m.timing === 'prophylactic');
  const takenMeds = todayLog?.medicationsTaken || [];

  // Today's weather from the log (auto-populated when saved)
  const todayWeather = todayLog && (todayLog.temperature !== null || todayLog.humidity !== null) ? todayLog : null;

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/favicon.svg" alt="" style={{ width: 28, height: 28 }} />
          AllergoAway
        </h1>
        {streak > 0 && (
          <div className="streak-badge">
            {streak} giorni
          </div>
        )}
      </div>

      {/* Today's status */}
      {todayLog ? (
        <div className="card" onClick={() => navigate(`/log?date=${todayString()}`)} style={{ cursor: 'pointer' }}>
          <div className="card-title">Oggi - {formatDateLong(new Date())}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className="score-circle" style={{ borderColor: getVASColor(todayLog.vasScore), width: 80, height: 80, margin: 0 }}>
              <span className="score-value" style={{ fontSize: 24 }}>{todayLog.vasScore}</span>
              <span className="score-label" style={{ fontSize: 9 }}>VAS</span>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: getVASColor(todayLog.vasScore) }}>
                {getVASLabel(todayLog.vasScore)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                Punteggio totale: {calculateTotalSymptomScore(todayLog)}/42
              </div>
              {todayLog.medicationsTaken.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  Farmaci: {todayLog.medicationsTaken.join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="card" onClick={() => navigate('/log')} style={{ cursor: 'pointer' }}>
          <div className="card-title">Oggi - {formatDateLong(new Date())}</div>
          <div className="empty-state" style={{ padding: '20px 0' }}>
            <h3>Nessuna registrazione</h3>
            <p>Tocca per registrare i tuoi sintomi di oggi</p>
          </div>
        </div>
      )}

      {/* Persistent FAB for quick logging */}
      <button className="fab" onClick={() => navigate(`/log?date=${todayString()}`)}>+</button>

      {/* Medication reminders */}
      {prophylacticMeds.length > 0 && (
        <div className="card">
          <div className="card-title">Farmaci giornalieri</div>
          {prophylacticMeds.map(med => {
            const taken = takenMeds.includes(med.name);
            return (
              <div key={med.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: '1px solid var(--border)'
              }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{med.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                    {med.type === 'antihistamine' ? 'Antistaminico' : med.type === 'nasal_spray' ? 'Spray nasale' : med.type === 'eye_drops' ? 'Collirio' : med.type === 'inhaler' ? 'Inalatore' : med.type === 'immunotherapy' ? 'Immunoterapia' : 'Altro'}
                  </span>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12,
                  background: taken ? 'rgba(102,187,106,0.15)' : 'rgba(255,167,38,0.15)',
                  color: taken ? 'var(--success)' : 'var(--warning)'
                }}>
                  {taken ? 'Assunto' : 'Da assumere'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Today's weather data */}
      {todayWeather && (
        <div className="card">
          <div className="card-title">Condizioni ambientali oggi</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {todayWeather.temperature !== null && (
              <WeatherItem label="Temperatura" value={`${todayWeather.temperature}\u00B0C`} />
            )}
            {todayWeather.humidity !== null && (
              <WeatherItem label="Umidita'" value={`${todayWeather.humidity}%`} />
            )}
            {todayWeather.windSpeed !== null && (
              <WeatherItem label="Vento" value={`${todayWeather.windSpeed} km/h`} />
            )}
            {todayWeather.rain !== null && todayWeather.rain > 0 && (
              <WeatherItem label="Pioggia" value={`${todayWeather.rain} mm`} />
            )}
            {todayWeather.pm25 !== null && (
              <WeatherItem label="PM2.5" value={`${todayWeather.pm25}`} />
            )}
            {todayWeather.uvIndex !== null && (
              <WeatherItem label="UV" value={`${todayWeather.uvIndex}`} />
            )}
          </div>
        </div>
      )}

      {/* Week sparkline */}
      {sparkData.some(d => d.vas !== null) && (
        <div className="card">
          <div className="card-title">Ultima settimana</div>
          <ResponsiveContainer width="100%" height={60}>
            <LineChart data={sparkData}>
              <YAxis domain={[0, 10]} hide />
              <Line
                type="monotone"
                dataKey="vas"
                stroke="var(--accent)"
                strokeWidth={2}
                dot={{ r: 3, fill: 'var(--accent)' }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
            {sparkData.map((d, i) => (
              <span key={i}>{format(new Date(d.date), 'EEE').substring(0, 2)}</span>
            ))}
          </div>
        </div>
      )}

      {/* Pollen alerts */}
      {activePollenList.length > 0 && (
        <div className="card">
          <div className="card-title">Pollini attivi questo mese</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {activePollenList.map(p => (
              <span key={p.name} className={`pollen-badge pollen-${p.level}`}>
                {p.name}: {p.level === 'VERY_HIGH' ? 'MOLTO ALTO' : p.level === 'HIGH' ? 'ALTO' : p.level === 'MEDIUM' ? 'MEDIO' : 'BASSO'}
              </span>
            ))}
          </div>
          {pollenAlerts.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--warning)' }}>
              Attenzione: {pollenAlerts.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <div className="card">
          <div className="card-title">Correlazioni e insights</div>
          {insights.slice(0, 3).map((insight, i) => (
            <div key={i} className="insight-card">
              <div className="insight-text">{insight}</div>
            </div>
          ))}
        </div>
      )}

      {daysToInsight > 0 && logs.length > 0 && (
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Ancora {daysToInsight} giorni per i primi insights!
          </div>
          <div style={{ marginTop: 8, height: 6, background: 'var(--bg-input)', borderRadius: 3 }}>
            <div style={{
              height: '100%', borderRadius: 3, background: 'var(--accent)',
              width: `${((7 - daysToInsight) / 7) * 100}%`, transition: 'width 0.3s'
            }} />
          </div>
        </div>
      )}

      {/* First time */}
      {logs.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <h3>Benvenuto in AllergoAway!</h3>
            <p>
              Il tuo tracker personale per le allergie. Inizia registrando i tuoi sintomi
              quotidianamente per scoprire correlazioni e pattern.
            </p>
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/settings')}>
              Configura il tuo profilo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function WeatherItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 70 }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}
